import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/grafana')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Grafana Cloud Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Grafana Cloud: Grafana Synthetic Monitoring is usage-based inside a full observability stack. Uptrack has flat plans from $3/mo." },
      { property: 'og:title', content: 'Grafana Cloud Alternative — Uptrack' },
      { property: 'og:description', content: "Grafana Cloud Pro starts at $19/mo plus usage. Uptrack Pro is $3/mo for 50 monitors at 30s, with status pages." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-grafana.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/grafana' },
    ],
    scripts: comparisonScripts('grafana', "Grafana Cloud"),
  }),
})

const data: ComparisonData = {
  competitor: "Grafana Cloud",
  tagline: "Grafana Cloud is an observability stack built on open-source tools, with synthetic monitoring based on k6. Uptrack is a smaller product for uptime and browser checks, with flat plans from $3/mo.",
  heroDescription: "These facts come from Grafana Cloud's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free tier", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "100k API + 10k browser test executions/mo" },
    { feature: "Starting price", uptrack: "$3/mo (Pro)", competitor: "$19/mo plus usage (Pro)", uptrackWins: true },
    { feature: "Monthly bill", uptrack: "Fixed per plan", competitor: "Usage-based ($5 per 10k extra API executions)", uptrackWins: true },
    { feature: "Enterprise", uptrack: "$79/mo (Business)", competitor: "$25,000/yr minimum commit", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "10 sec" },
    { feature: "Alert confirmation", uptrack: "2-5 consecutive failed checks", competitor: "Sensitivity levels (share of probes failing for 5 min) or custom rules" },
    { feature: "k6 scripted checks", uptrack: "Not available", competitor: "Full k6 scripting" },
    { feature: "Dashboards", uptrack: "Built-in monitoring views", competitor: "Custom Grafana dashboards" },
    { feature: "Metrics/logs/traces", uptrack: "Not available", competitor: "Full observability stack" },
  ],

  painPoints: [
    {
      title: "Usage-based pricing",
      description: "Grafana Cloud bills metrics, logs, traces, and synthetic test executions as separate usage. One execution is one test at one probe location. Uptrack plans have a fixed monthly price.",
    },
    {
      title: "Enterprise minimum",
      description: "Grafana Cloud Enterprise has a minimum commit of $25,000 per year. Uptrack's largest plan, Business, is $79/mo ($588/yr billed annually).",
    },
    {
      title: "Scripted checks use k6",
      description: "Grafana's scripted and browser checks use k6, which means writing JavaScript. Uptrack's HTTP, keyword, TCP, DNS, and SSL monitors are set up in a web form.",
    },
  ],

  differentiators: [
    "Status pages included on every plan: 5 on Pro, unlimited on Team and Business.",
    "Simple setup in minutes. HTTP monitors need no scripts.",
    "Flat pricing: $3/mo to $79/mo. No usage dimensions to track.",
    "Alert confirmation with 2-5 consecutive failed checks.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need full observability with metrics, logs, and traces in one platform, Grafana Cloud covers all of them.",
    "If you need custom dashboards, Grafana's visualization is far more flexible than Uptrack's built-in views.",
    "If you need k6 load testing alongside synthetic monitoring.",
    "If your team already uses Prometheus, Loki, or Tempo and wants a managed Grafana stack.",
    "If you need check intervals under 30 seconds, Grafana Synthetic Monitoring can check every 10 seconds.",
  ],
}
